import React, { useState, useEffect, useCallback } from 'react';
import Modal from './modal.js';
import { BBCodeParser, useBggUsers } from '../helpers';
import { Comment } from '../objects';
import { fetchData } from '../services';
import PriceHistory from './price-history.js';
import BggListingMetadata from './bgg-listing-metadata.js';
import { toast } from 'react-toastify';

const BGG_BASE_URL = "https://boardgamegeek.com";

const BggGameModal = ({ isModalOpen, setIsModalOpen, modalData, onFilterToSeller, updateDealScore }) => {
    const { findBggUser } = useBggUsers();

    const [modalComments, setModalComments] = useState([]);
    const [isCommentsLoading, setIsCommentsLoading] = useState(false);
    const [averagePrice, setAveragePrice] = useState(null);
    const [modalDealScore, setModalDealScore] = useState(null);
    const [isDealScoreLoading, setIsDealScoreLoading] = useState(false);

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const readyModalData = useCallback(async () => {
        setModalComments([]);
        setIsCommentsLoading(true);
        setAveragePrice(null);
        setModalDealScore(modalData?.dealScore || null);

        const id = modalData?.id;
        if (!id) {
            setIsCommentsLoading(false);
            return;
        }

        try {
            const commentsData = await fetchData("/api/listitems/".concat(id, "/comments"));
            if (commentsData && commentsData.comments) {
                let commentsList = [];
                for (let index = 0; index < commentsData.comments.length; index++) {
                    let comment = commentsData.comments[index];
                    let author = await findBggUser(comment.author);
                    commentsList.push(new Comment(author.username, comment.body, comment.postdate));
                }
                setModalComments(commentsList);
            } else {
                setModalComments([]);
            }
        } catch (error) {
            console.error("Error fetching comments:", error);
            toast.error("Error fetching comments. Please try again.", { autoClose: false });
        } finally {
            setIsCommentsLoading(false);
        }
    }, [modalData, findBggUser]);

    const onAveragePriceLoaded = (price) => {
        setAveragePrice(price);
    };

    const handleSellerClick = (seller) => {
        if (onFilterToSeller) {
            onFilterToSeller(seller);
        }
        closeModal();
    };

    useEffect(() => {
        if (isModalOpen) {
            readyModalData();

            // Populate/use the grid's deal score for this listing so modal math matches the table,
            // including any linked extras in the listing description.
            if (
                updateDealScore &&
                modalData &&
                modalData.item &&
                (!modalData.dealScore || !modalData.dealScore.score)
            ) {
                setIsDealScoreLoading(true);
                updateDealScore(modalData)
                    .then((dealScore) => {
                        setModalDealScore(dealScore || modalData.dealScore || null);
                    })
                    .finally(() => {
                        setIsDealScoreLoading(false);
                    });
            } else {
                setModalDealScore(modalData?.dealScore || null);
                setIsDealScoreLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen]);

    const hasItem = modalData && modalData.item && modalData.item.id !== undefined;
    const gameBggUrl = hasItem ? `${BGG_BASE_URL}/boardgame/${modalData.item.id}` : null;
    const modalTitle = modalData?.item?.name || 'Game details';
    const modalDescription = modalData?.body || '';

    return (
        <Modal isOpen={isModalOpen} onClose={closeModal} title={modalTitle} titleLink={gameBggUrl} buttonLink={BGG_BASE_URL.concat(modalData?.href || '')}>
            <div className="game-modal-content">
                {modalData && modalData.item && modalData.item.image && (
                    <div className="game-image-container" style={{ textAlign: 'center', marginBottom: '16px' }}>
                        {gameBggUrl ? (
                            <a href={gameBggUrl} target="_blank" rel="noreferrer">
                                <img
                                    src={modalData.item.image}
                                    alt={modalData.item.name || 'Game image'}
                                    style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', cursor: 'pointer' }}
                                />
                            </a>
                        ) : (
                            <img
                                src={modalData.item.image}
                                alt={modalData.item.name || 'Game image'}
                                style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }}
                            />
                        )}
                    </div>
                )}

                {hasItem && (
                    <div className="modal-section">
                        <BggListingMetadata
                            item={modalData}
                            averagePrice={averagePrice}
                            dealScore={modalDealScore}
                            isAveragePriceLoading={isDealScoreLoading}
                            onSellerClick={handleSellerClick}
                        />
                    </div>
                )}

                {hasItem && (
                    <div className="modal-section">
                        <PriceHistory
                            gameId={modalData.item.id}
                            onAveragePriceLoaded={onAveragePriceLoaded}
                        />
                    </div>
                )}

                <div className="modal-section">
                    <h3 className="section-title">Description</h3>
                    <div className="game-description">
                        <BBCodeParser bbcode={modalDescription} />
                    </div>
                </div>

                <div className="modal-section">
                    <h3 className="section-title">Comments</h3>
                    {isCommentsLoading ? (
                        <div className="comments-loading-placeholder">
                            <div className="loader" style={{ width: '18px', height: '18px' }}></div>
                            <span>Loading comments...</span>
                        </div>
                    ) : modalComments.length > 0 ? (
                        <div className="comments-table-container">
                            <table className="comments-table">
                                <colgroup>
                                    <col style={{ width: '20%' }} />
                                    <col style={{ width: '20%' }} />
                                    <col style={{ width: '60%' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>Time</th>
                                        <th>Author</th>
                                        <th>Comment</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modalComments.map((comment, index) => (
                                        <tr key={index}>
                                            <td>
                                                <div>{new Date(comment.time).toLocaleDateString()}</div>
                                                <div>{new Date(comment.time).toLocaleTimeString()}</div>
                                            </td>
                                            <td>{comment.author}</td>
                                            <td className="comment-body">{comment.body}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="no-comments">There are no comments on this list item</p>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default BggGameModal;
